import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  Radio,
  Progress,
} from '@mantine/core'
import { CSVLink } from 'react-csv'
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'

type DownloadStatus = 'idle' | 'loading' | 'success' | 'error'

interface DownloadOptionGroup {
  label: string
  name: string
  options: { label: string; value: string; disabled?: boolean }[]
  defaultValue: string
  direction?: 'horizontal' | 'vertical'
}

const defaultOptionGroups: DownloadOptionGroup[] = [
  {
    label: 'File Format',
    name: 'fileFormat',
    options: [
      { label: 'CSV', value: 'csv' },
      { label: 'XLS', value: 'xls', disabled: true },
    ],
    defaultValue: 'csv',
    direction: 'horizontal',
  },
  {
    label: 'Trade Level',
    name: 'reportType',
    options: [
      { label: 'Master', value: 'master' },
      { label: 'SubTrade', value: 'subtrade' },
      { label: 'Full Extract', value: 'fullExtract', disabled: true },
    ],
    defaultValue: 'master',
    direction: 'vertical',
  },
]

interface GenerateDownloadModalProps {
  opened: boolean
  onClose: () => void
  onGenerate?: (selections: Record<string, string>) => Promise<object[]>
  onDownload?: (selections: Record<string, string>) => Promise<boolean>
  title?: string
  subtitle?: string
  optionGroups?: DownloadOptionGroup[]
  generateButtonLabel?: string
  successMessage?: string
  errorMessage?: string
  filename?: string
}

function sanitizeCsvValue(value: unknown): string {
  const str = String(value ?? '')
  if (
    str.startsWith('=') ||
    str.startsWith('+') ||
    str.startsWith('-') ||
    str.startsWith('@')
  ) {
    return `'${str}`
  }
  return str
}

function GenerateDownloadModal({
  opened,
  onClose,
  onGenerate,
  onDownload,
  title = 'Generate & Download',
  subtitle = 'You are about to download a Trade Report.',
  optionGroups = defaultOptionGroups,
  generateButtonLabel = 'Generate & Download',
  successMessage = 'Report successfully generated and downloaded.',
  errorMessage = 'File failed to be generated',
  filename = 'trade-report.csv',
}: GenerateDownloadModalProps) {
  const [selections, setSelections] = useState<Record<string, string>>(() =>
    Object.fromEntries(optionGroups.map((g) => [g.name, g.defaultValue])),
  )
  const [status, setStatus] = useState<DownloadStatus>('idle')
  const [progress, setProgress] = useState(0)
  const [csvData, setCsvData] = useState<any[]>([])
  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null)
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  )

  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }, [])

  const errorRef = useRef(errorMessage)

  useEffect(() => {
    if (!opened) {
      setStatus('idle')
      setProgress(0)
      setCsvData([])
      errorRef.current = errorMessage
      clearProgressInterval()
    }
  }, [opened, clearProgressInterval])

  useEffect(() => {
    return clearProgressInterval
  }, [clearProgressInterval])

  const handleSelectionChange = (name: string, value: string) => {
    setSelections((prev) => ({ ...prev, [name]: value }))
  }

  const startProgress = () => {
    setProgress(0)
    clearProgressInterval()
    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearProgressInterval()
          return 90
        }
        return prev + 10
      })
    }, 200)
  }

  const handleGenerate = async () => {
    setStatus('loading')
    startProgress()
    try {
      if (onDownload) {
        const success = await onDownload(selections)
        if (!success) {
          throw new Error('Download returned no data')
        }

        clearProgressInterval()
        setProgress(100)
        setStatus('success')
      } else if (onGenerate) {
        const data = await onGenerate(selections)
        if (!data.length) {
          throw new Error('No data available to export')
        }

        const headers = Object.keys(data[0])
        const rows = data.map((row) =>
          Object.values(row).map(sanitizeCsvValue),
        )
        setCsvData([headers, ...rows])

        clearProgressInterval()
        setProgress(100)
        setStatus('success')

        csvLinkRef.current?.link?.click()
      }
      // onClose()
    } catch (e: any) {
      console.error(e)
      if (e?.message) errorRef.current = e.message
      clearProgressInterval()
      setProgress(100)
      setStatus('error')
    }
  }

  const isFinished = status === 'success' || status === 'error'
  const showProgress = status !== 'idle'
  const showGenerateButton = status !== 'success'
  const cancelLabel = isFinished ? 'Close' : 'Cancel'

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      size="35%"
    >
      <Stack w="100%" p={40} spacing={20}>
        <div className="generate-download-header">
          <Text className="generate-download-title">{title}</Text>
          <Button
            onClick={onClose}
            variant="subtle"
            style={{
              padding: 0,
              background: 'transparent',
              height: 30,
              width: 30,
              minWidth: 'auto',
            }}
            aria-label="Close modal"
          >
            <CloseIcon style={{ width: '30px' }} />
          </Button>
        </div>
        <div className="generate-download-content">
          <Text className="generate-download-subtitle">{subtitle}</Text>
          <div className="generate-download-options-container">
            {optionGroups.map((group) => (
              <Radio.Group
                key={group.name}
                label={group.label}
                value={selections[group.name]}
                onChange={(value: string) =>
                  handleSelectionChange(group.name, value)
                }
                classNames={{
                  label: 'generate-download-group-label',
                }}
              >
                <Group
                  mt="xs"
                  spacing="lg"
                  style={{
                    flexDirection:
                      group.direction === 'horizontal' ? 'row' : 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  {group.options.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      label={option.label}
                      disabled={option.disabled}
                      classNames={{
                        inner: 'generate-download-radio-inner',
                        radio: 'generate-download-radio',
                        label: 'generate-download-radio-label',
                      }}
                    />
                  ))}
                </Group>
              </Radio.Group>
            ))}
          </div>
          {showProgress && (
            <div className="generate-download-progress">
              <Text className="generate-download-progress-header">
                Progress...
              </Text>
              <Progress
                w="100%"
                value={progress}
                h="30px"
                label={`${Math.round(progress)}%`}
                size="xl"
                radius="xl"
                classNames={{
                  root: 'generate-download-progress-root',
                  label: 'generate-download-progress-label',
                }}
              />
              {isFinished && (
                <div className="generate-download-status-message">
                  <Text
                    className={
                      status === 'success'
                        ? 'generate-download-status-success'
                        : 'generate-download-status-error'
                    }
                  >
                    <span className="generate-download-status-prefix">
                      {status === 'success' ? 'Success: ' : 'Error: '}
                    </span>
                    {status === 'success' ? successMessage : errorRef.current}
                  </Text>
                </div>
              )}
            </div>
          )}
        </div>
        <Group position="right" mt="20px">
          <Button
            className="generate-download-cancel-button"
            onClick={onClose}
          >
            <div className="generate-download-cancel-button-text">
              {cancelLabel}
            </div>
          </Button>
          {showGenerateButton && (
            <Button
              className="generate-download-submit-button"
              onClick={handleGenerate}
              loading={status === 'loading'}
            >
              <div className="generate-download-submit-button-text">
                {generateButtonLabel}
              </div>
            </Button>
          )}
        </Group>
      </Stack>
      <CSVLink
        ref={csvLinkRef}
        data={csvData}
        filename={filename}
        style={{ display: 'none' }}
      />
    </Modal>
  )
}

export default GenerateDownloadModal
